import { useCallback, useEffect, useRef, useState } from 'react';
import type { AutoJoinPrefs, CalendarEventDTO } from '../../global';
import { t } from '../../lib/i18n';

const EMPTY_PREFS: AutoJoinPrefs = { autoJoinBot: false, autoJoinExcluded: [], autoJoinIncluded: [] };

export interface ScheduledBot {
  meetingId: string;
  status: string;
}

interface AgendaState {
  events: CalendarEventDTO[];
  /** Takvim etkinliği kimliği → planlanmış bot toplantısı. */
  scheduled: Record<string, ScheduledBot>;
  prefs: AutoJoinPrefs;
  isLoading: boolean;
  /** Takvim okunamadıysa nedeni (bağlı değil / yetki süresi doldu / ağ). */
  calendarErrorCode: string | null;
}

/** Mesaj çizim anında üretiliyor ki dil değişince de güncel kalsın. */
function calendarErrorMessage(code: string): string {
  switch (code) {
    case 'not_connected':
      return t('Google Takvim bağlı değil.', 'Google Calendar is not connected.');
    case 'auth_expired':
      return t(
        'Google Takvim yetkisinin süresi doldu — Entegrasyonlar ekranından yeniden bağlanın.',
        'Google Calendar access has expired — reconnect it from the Integrations screen.'
      );
    default:
      return t('Takvim şu an okunamadı.', 'The calendar can’t be read right now.');
  }
}

/**
 * Günlük Özet'in "Bugünün Programı": bugünün takvim etkinlikleri (Google +
 * Outlook), hangilerine bot planlandığı ve otomatik katılım tercihleri.
 * Arka planda bot durumu değişince (meetingsChanged) sessizce tazelenir.
 */
export function useTodayAgenda(enabled: boolean) {
  const [state, setState] = useState<AgendaState>({
    events: [],
    scheduled: {},
    prefs: EMPTY_PREFS,
    isLoading: enabled,
    calendarErrorCode: null,
  });
  const mountedRef = useRef(true);

  const load = useCallback(async (silent = false) => {
    if (!enabled) return;
    if (!silent) setState((prev) => ({ ...prev, isLoading: true }));

    const [events, scheduled, prefs] = await Promise.all([
      window.zabit.getTodayCalendarEvents().catch(() => ({ error: 'network' as const })),
      window.zabit.getBotScheduleMap().catch(() => ({})),
      window.zabit.getAutoJoinPrefs().catch(() => EMPTY_PREFS),
    ]);
    if (!mountedRef.current) return;

    setState({
      events: Array.isArray(events) ? events : [],
      calendarErrorCode: Array.isArray(events) ? null : events.error,
      scheduled,
      prefs,
      isLoading: false,
    });
  }, [enabled]);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    const unsubscribe = window.zabit.onMeetingsChanged(() => void load(true));
    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, [load]);

  /** Etkinliğe hemen (ya da başlangıç saatine planlı) bot gönderir. */
  const sendBot = useCallback(async (event: CalendarEventDTO) => {
    if (!event.conference) throw new Error(t('Bu etkinliğin toplantı linki yok.', 'This event has no meeting link.'));
    await window.zabit.sendBot({
      meetingUrl: event.conference.url,
      title: event.title,
      startISO: event.isAllDay ? undefined : event.startISO,
      calendarEventId: event.id,
      participants: (event.attendees ?? []).map((a) => ({
        name: a.name || a.email.split('@')[0],
        email: a.email,
      })),
    });
    await load(true);
  }, [load]);

  /**
   * Toplantının kendi bot seçimi. Kapatılınca planlanmış ve henüz girmemiş bot
   * da iptal edilir; açılınca genel ayar kapalı olsa da bot gider.
   */
  const setEventJoin = useCallback(async (event: CalendarEventDTO, join: boolean) => {
    const prefs = await window.zabit.setAutoJoinEvent(event.id, join);
    setState((prev) => ({ ...prev, prefs }));
    await load(true);
  }, [load]);

  const { calendarErrorCode, ...rest } = state;
  return {
    ...rest,
    calendarError: calendarErrorCode ? calendarErrorMessage(calendarErrorCode) : null,
    reload: load,
    sendBot,
    setEventJoin,
  };
}
